import React, { useState, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Stack,
  Input,
  Select,
  Option,
  FormControl,
  FormLabel,
  IconButton,
  Chip,
  Alert,
  Divider
} from '@mui/joy';
import {
  Plus,
  Trash as Trash2,
  Envelope as Mail,
  User,
  ArrowUp,
  ArrowDown,
  Eye,
  XCircle as AlertCircle
} from '@phosphor-icons/react';
import type { Contract } from '../../../types';

export interface ContractRecipient {
  id: string;
  email: string;
  name: string;
  role: 'signer' | 'cc' | 'approver';
  signerType?: string; // Original signer type (CUSTOMER, SALES_REP, etc.)
  signing_order: number;
  status: 'pending' | 'sent' | 'viewed' | 'signed' | 'declined';
  sent_date?: string;
  viewed_date?: string;
  signed_date?: string;
  message?: string;
}

// Backend roles that never sign ('customer', 'sales_rep', 'manager', ... all do)
const NON_SIGNING_ROLES = ['cc', 'approver'];

export const isSigningRole = (role?: string) => !NON_SIGNING_ROLES.includes(role || 'signer');

interface RecipientManagerProps {
  contract: Contract;
  onRecipientsChange: (recipients: ContractRecipient[]) => void;
  readOnly?: boolean;
}

export const RecipientManager: React.FC<RecipientManagerProps> = ({
  contract,
  onRecipientsChange,
  readOnly = false
}) => {
  // Recipients stored on the contract are plain JSON with no id, but every handler
  // here keys off one: without it removing a recipient drops the whole list and
  // reordering always moves the first row. Email is the backend's own identity for
  // a recipient, so reuse it.
  const [recipients, setRecipients] = useState<ContractRecipient[]>(
    (contract.recipients || []).map(r => ({
      ...r,
      id: r.id || r.email || crypto.randomUUID(),
      status: r.status || 'pending',
      role: r.role || 'signer'
    }))
  );
  const [newRecipient, setNewRecipient] = useState({
    email: '',
    name: '',
    role: 'signer' as ContractRecipient['role'],
    message: ''
  });
  const [showAddForm, setShowAddForm] = useState(false);

  const handleAddRecipient = useCallback(() => {
    if (!newRecipient.email || !newRecipient.name) return;

    const recipient: ContractRecipient = {
      id: crypto.randomUUID(),
      email: newRecipient.email,
      name: newRecipient.name,
      role: newRecipient.role,
      signing_order: recipients.filter(r => isSigningRole(r.role)).length + 1,
      status: 'pending',
      message: newRecipient.message
    };

    const updatedRecipients = [...recipients, recipient];
    setRecipients(updatedRecipients);
    onRecipientsChange(updatedRecipients);

    // Reset form
    setNewRecipient({
      email: '',
      name: '',
      role: 'signer',
      message: ''
    });
    setShowAddForm(false);
  }, [newRecipient, recipients, onRecipientsChange]);

  const handleRemoveRecipient = useCallback((recipientId: string) => {
    const updatedRecipients = recipients.filter(r => r.id !== recipientId);
    // Reorder signing order
    const reorderedRecipients = updatedRecipients.map((r, index) => 
      isSigningRole(r.role) ? { ...r, signing_order: index + 1 } : r
    );
    
    setRecipients(reorderedRecipients);
    onRecipientsChange(reorderedRecipients);
  }, [recipients, onRecipientsChange]);

  const handleMoveRecipient = useCallback((recipientId: string, direction: 'up' | 'down') => {
    const signers = recipients.filter(r => isSigningRole(r.role));
    const currentIndex = signers.findIndex(r => r.id === recipientId);
    
    if (
      (direction === 'up' && currentIndex === 0) ||
      (direction === 'down' && currentIndex === signers.length - 1)
    ) {
      return;
    }

    const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    const updatedSigners = [...signers];
    [updatedSigners[currentIndex], updatedSigners[newIndex]] = 
    [updatedSigners[newIndex], updatedSigners[currentIndex]];

    // Update signing order
    updatedSigners.forEach((signer, index) => {
      signer.signing_order = index + 1;
    });

    // Merge back with non-signers
    const nonSigners = recipients.filter(r => !isSigningRole(r.role));
    const updatedRecipients = [...updatedSigners, ...nonSigners];
    
    setRecipients(updatedRecipients);
    onRecipientsChange(updatedRecipients);
  }, [recipients, onRecipientsChange]);

  const getStatusColor = (status: ContractRecipient['status']) => {
    switch (status) {
      case 'signed': return 'success';
      case 'viewed': return 'primary';
      case 'sent': return 'warning';
      case 'declined': return 'danger';
      default: return 'neutral';
    }
  };

  const getStatusIcon = (status: ContractRecipient['status']) => {
    switch (status) {
      case 'signed': return '✓';
      case 'viewed': return <Eye size={12} />;
      case 'sent': return <Mail size={12} />;
      case 'declined': return '✗';
      default: return '○';
    }
  };

  const signers = recipients.filter(r => isSigningRole(r.role)).sort((a, b) => a.signing_order - b.signing_order);
  const nonSigners = recipients.filter(r => !isSigningRole(r.role));
  const hasRequiredFields = contract.contract_fields.some(f => f.required);

  return (
    <Card variant="outlined">
      <CardContent>
        <Stack direction="row" spacing={2} justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
          <Typography level="title-md">
            Recipients & Signing Order
          </Typography>
          
          {!readOnly && (
            <Button
              size="sm"
              variant="outlined"
              color="primary"
              startDecorator={<Plus size={16} />}
              onClick={() => setShowAddForm(true)}
              disabled={showAddForm}
            >
              Add Recipient
            </Button>
          )}
        </Stack>

        {/* Add Recipient Form */}
        {showAddForm && !readOnly && (
          <Card variant="soft" color="primary" size="sm" sx={{ mb: 3 }}>
            <CardContent>
              <Typography level="title-sm" sx={{ mb: 2 }}>
                Add New Recipient
              </Typography>
              
              <Stack spacing={2}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <FormControl sx={{ flex: 1 }}>
                    <FormLabel>Name</FormLabel>
                    <Input
                      placeholder="John Doe"
                      value={newRecipient.name}
                      onChange={(e) => setNewRecipient(prev => ({ ...prev, name: e.target.value }))}
                    />
                  </FormControl>
                  
                  <FormControl sx={{ flex: 1 }}>
                    <FormLabel>Email</FormLabel>
                    <Input
                      type="email"
                      placeholder="john@example.com"
                      value={newRecipient.email}
                      onChange={(e) => setNewRecipient(prev => ({ ...prev, email: e.target.value }))}
                    />
                  </FormControl>
                </Stack>

                <FormControl>
                  <FormLabel>Role</FormLabel>
                  <Select
                    value={newRecipient.role}
                    onChange={(_, value) => setNewRecipient(prev => ({ ...prev, role: value as ContractRecipient['role'] }))}
                  >
                    <Option value="signer">Signer (must sign)</Option>
                    <Option value="cc">CC (receives copy)</Option>
                    <Option value="approver">Approver (must approve)</Option>
                  </Select>
                </FormControl>

                <FormControl>
                  <FormLabel>Personal Message (Optional)</FormLabel>
                  <Input
                    placeholder="Please review and sign this contract..."
                    value={newRecipient.message}
                    onChange={(e) => setNewRecipient(prev => ({ ...prev, message: e.target.value }))}
                  />
                </FormControl>

                <Stack direction="row" spacing={1} justifyContent="flex-end">
                  <Button
                    size="sm"
                    variant="outlined"
                    onClick={() => setShowAddForm(false)}
                  >
                    Cancel
                  </Button>
                  <Button
                    size="sm"
                    variant="solid"
                    onClick={handleAddRecipient}
                    disabled={!newRecipient.email || !newRecipient.name}
                  >
                    Add Recipient
                  </Button>
                </Stack>
              </Stack>
            </CardContent>
          </Card>
        )}

        {/* Signers List */}
        {signers.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Typography level="title-sm" sx={{ mb: 2 }}>
              Signing Order
            </Typography>
            
            <Stack spacing={1}>
              {signers.map((recipient, index) => (
                <Card key={recipient.id} variant="outlined" size="sm">
                  <CardContent sx={{ p: { xs: 0, sm: 2 } }}>
                    <Stack direction="row" gap={1.5} alignItems="center" flexWrap="wrap">
                      <Box
                        sx={{
                          width: 24,
                          flexShrink: 0,
                          height: 24,
                          borderRadius: '50%',
                          backgroundColor: 'primary.100',
                          color: 'var(--brand-text, #7013f5)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontWeight: 'lg',
                          fontSize: 'sm'
                        }}
                      >
                        {recipient.signing_order}
                      </Box>
                      
                      <Box sx={{ flex: 1, minWidth: 120, overflowWrap: 'anywhere' }}>
                        <Typography level="body-sm" fontWeight="lg">
                          {recipient.name}
                        </Typography>
                        <Typography level="body-xs" textColor="text.secondary">
                          {recipient.email}
                        </Typography>
                      </Box>
                      
                      <Chip
                        color={getStatusColor(recipient.status || 'pending')}
                        size="sm"
                        startDecorator={getStatusIcon(recipient.status || 'pending')}
                      >
                        {(recipient.status || 'pending').charAt(0).toUpperCase() + (recipient.status || 'pending').slice(1)}
                      </Chip>
                      
                      {!readOnly && (
                        <Stack direction="row" spacing={0.5} sx={{ flexBasis: { xs: '100%', sm: 'auto' }, justifyContent: 'flex-end' }}>
                          <IconButton
                            aria-label={`Move ${recipient.name} earlier`}
                            size="sm"
                            variant="outlined"
                            onClick={() => handleMoveRecipient(recipient.id, 'up')}
                            disabled={index === 0}
                          >
                            <ArrowUp size={14} />
                          </IconButton>
                          <IconButton
                            aria-label={`Move ${recipient.name} later`}
                            size="sm"
                            variant="outlined"
                            onClick={() => handleMoveRecipient(recipient.id, 'down')}
                            disabled={index === signers.length - 1}
                          >
                            <ArrowDown size={14} />
                          </IconButton>
                          <IconButton
                            aria-label={`Remove ${recipient.name}`}
                            size="sm"
                            variant="outlined"
                            color="danger"
                            onClick={() => handleRemoveRecipient(recipient.id)}
                          >
                            <Trash2 size={14} />
                          </IconButton>
                        </Stack>
                      )}
                    </Stack>
                    
                    {recipient.message && (
                      <Typography level="body-xs" sx={{ mt: 1, fontStyle: 'italic' }}>
                        "{recipient.message}"
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              ))}
            </Stack>
          </Box>
        )}

        {/* Non-Signers (CC, Approvers) */}
        {nonSigners.length > 0 && (
          <Box>
            <Divider sx={{ my: 2 }} />
            
            <Typography level="title-sm" sx={{ mb: 2 }}>
              Other Recipients
            </Typography>
            
            <Stack spacing={1}>
              {nonSigners.map((recipient) => (
                <Card key={recipient.id} variant="outlined" size="sm">
                  <CardContent sx={{ p: { xs: 0, sm: 2 } }}>
                    <Stack direction="row" gap={1.5} alignItems="center" flexWrap="wrap">
                      <User size={16} color="var(--joy-palette-neutral-500)" />
                      
                      <Box sx={{ flex: 1, minWidth: 120, overflowWrap: 'anywhere' }}>
                        <Typography level="body-sm" fontWeight="lg">
                          {recipient.name}
                        </Typography>
                        <Typography level="body-xs" textColor="text.secondary">
                          {recipient.email} • {(recipient.signerType || recipient.role).toUpperCase()}
                        </Typography>
                      </Box>
                      
                      <Chip
                        color={getStatusColor(recipient.status || 'pending')}
                        size="sm"
                        startDecorator={getStatusIcon(recipient.status || 'pending')}
                      >
                        {(recipient.status || 'pending').charAt(0).toUpperCase() + (recipient.status || 'pending').slice(1)}
                      </Chip>
                      
                      {!readOnly && (
                        <IconButton
                          aria-label={`Remove ${recipient.name}`}
                          size="sm"
                          variant="outlined"
                          color="danger"
                          onClick={() => handleRemoveRecipient(recipient.id)}
                        >
                          <Trash2 size={14} />
                        </IconButton>
                      )}
                    </Stack>
                  </CardContent>
                </Card>
              ))}
            </Stack>
          </Box>
        )}

        {/* Empty State */}
        {recipients.length === 0 && (
          <Alert color="neutral">
            <AlertCircle size={16} />
            <Typography level="body-sm">
              No recipients added yet. Add recipients to send this contract for signing.
            </Typography>
          </Alert>
        )}

        {/* Validation Warnings */}
        {recipients.length > 0 && !hasRequiredFields && (
          <Alert color="warning" sx={{ mt: 2 }}>
            <AlertCircle size={16} />
            <Typography level="body-sm">
              This contract has no required fields. Consider adding signature fields before sending.
            </Typography>
          </Alert>
        )}
      </CardContent>
    </Card>
  );
};

export default RecipientManager;
